/**
 * ToolbarIndicatorsGroup — the six indicator groups as ONE toolbar control.
 *
 * WHY
 * Bands & Levels, Trend, Oscillators, Overlays, Options and Signals each used to
 * render their own ToolbarMenuGroup: six labelled containers, six ★-favourite
 * sets, six dropdown triggers. Together they filled an entire toolbar row — at
 * 1366px the toolbar ran to four rows and the chart was left with half the
 * screen. They are also all the same KIND of thing ("what is drawn on the
 * chart"), so six separate containers bought nothing.
 *
 * This renders one container: every pinned favourite still sits inline and one
 * click away, followed by a single trigger that opens all six sections in one
 * panel. One row instead of two, with nothing moved further than it was.
 *
 * FAVOURITES ARE PRESERVED. The six per-group keys
 * (`pwa.state.v1.chart.toolbarFav.<id>.v1`) are read once and merged into this
 * component's own key, so pins a user set before this change survive it.
 */
import React, { useState, useMemo } from 'react';
import { Box, Chip, Popover, Typography, IconButton, Tooltip, Badge, Divider } from '@mui/material';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { usePersistedState } from '../../hooks/usePersistedState';
import { useAppTheme } from '../../hooks/useAppTheme';
import { CH_TYPE, chGroupSx, chGroupLabelSx, chChipSx } from '../../styles/appUi';
import type { ToolbarItem } from './ToolbarMenuGroup';

export interface IndicatorSection {
  /** Must stay the ORIGINAL ToolbarMenuGroup groupId — that is what the
   *  favourites migration below reads. */
  id: string;
  label: string;
  items: ToolbarItem[];
  defaultFavourites?: string[];
}

interface Props {
  sections: IndicatorSection[];
  isDark: boolean;
}

const LEGACY_PREFIX = 'pwa.state.v1.chart.toolbarFav.';

const ToolbarIndicatorsGroup: React.FC<Props> = ({ sections, isDark }) => {
  const { C } = useAppTheme();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);

  /* One key for all six sections. Seeded from whatever the user had pinned in
     the per-group keys, so nobody loses their toolbar on upgrade. */
  const [favMap, setFavMap] = usePersistedState<Record<string, string[]>>(
    'chart.toolbarFav.indicators.v1',
    () => {
      const out: Record<string, string[]> = {};
      for (const s of sections) {
        let pins: string[] | null = null;
        try {
          const raw = localStorage.getItem(LEGACY_PREFIX + s.id + '.v1');
          if (raw) pins = JSON.parse(raw);
        } catch { /* corrupt entry — fall through to the default */ }
        out[s.id] = Array.isArray(pins) ? pins : (s.defaultFavourites || []);
      }
      return out;
    },
  );

  const isPinned = (sectionId: string, key: string) => (favMap[sectionId] || []).includes(key);
  const togglePin = (sectionId: string, key: string) => setFavMap(prev => {
    const cur = prev[sectionId] || [];
    return { ...prev, [sectionId]: cur.includes(key) ? cur.filter(k => k !== key) : [...cur, key] };
  });

  /** Pinned items across every section, in section order. */
  const pinned = useMemo(
    () => sections.flatMap(s => s.items.filter(it => isPinned(s.id, it.key))),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [sections, favMap],
  );

  const activeCount = sections.reduce((n, s) => n + s.items.filter(i => i.active).length, 0);
  /* A dot when something is ON but not pinned to the bar — otherwise an active
     indicator could be invisible, which is how you end up wondering why the
     chart is drawing something you cannot find the switch for. */
  const hiddenActive = sections.some(s => s.items.some(i => i.active && !isPinned(s.id, i.key)));

  return (
    <Box sx={chGroupSx(isDark)}>
      <Typography sx={chGroupLabelSx}>Indicators</Typography>

      {pinned.map(it => (
        <Tooltip key={it.key} title={it.tooltip || ''} arrow disableHoverListener={!it.tooltip}>
          <Chip label={it.label} size="small" variant="outlined" onClick={it.onClick}
            sx={chChipSx(C, isDark, { active: it.active, tone: it.tone })} />
        </Tooltip>
      ))}

      <Tooltip arrow title="All indicators — toggle & pin ★ favourites to the bar">
        <Badge color="warning" variant="dot" overlap="circular" invisible={!hiddenActive}>
          <Chip size="small" variant="outlined"
            onClick={(e) => setAnchor(e.currentTarget)}
            icon={<ArrowDropDownIcon sx={{ fontSize: 16 }} />}
            label={activeCount > 0 ? `${activeCount}` : 'All'}
            sx={{ ...chChipSx(C, isDark), fontWeight: 800, '& .MuiChip-icon': { ml: 0.2, mr: -0.4 } }}
          />
        </Badge>
      </Tooltip>

      <Popover
        open={!!anchor}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{ paper: { sx: { p: 1, maxHeight: 560, width: 292 } } }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
          <Typography sx={{ fontSize: CH_TYPE.body, fontWeight: 800 }}>Indicators</Typography>
          <Typography sx={{ fontSize: CH_TYPE.micro, color: 'text.secondary', ml: 'auto' }}>★ pin to the bar</Typography>
        </Box>
        <Divider sx={{ mb: 0.5 }} />

        {sections.map((s, si) => (
          <Box key={s.id} sx={{ mb: si === sections.length - 1 ? 0 : 0.9 }}>
            <Typography sx={{ ...chGroupLabelSx, display: 'block', mb: 0.35, mr: 0 }}>{s.label}</Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.2 }}>
              {s.items.map(it => {
                const pin = isPinned(s.id, it.key);
                return (
                  <Box key={it.key} sx={{
                    display: 'flex', alignItems: 'center', gap: 0.5, px: 0.3, py: 0.15,
                    borderRadius: 1, '&:hover': { bgcolor: 'action.hover' },
                  }}>
                    <Tooltip arrow title={it.tooltip || ''} disableHoverListener={!it.tooltip} placement="left">
                      <Chip label={it.label} size="small" variant="outlined" onClick={it.onClick}
                        sx={{ ...chChipSx(C, isDark, { active: it.active, tone: it.tone }),
                              flex: 1, justifyContent: 'flex-start',
                              '& .MuiChip-label': { px: 0.6, width: '100%' } }} />
                    </Tooltip>
                    <Tooltip arrow title={pin ? 'Unpin from bar' : 'Pin to bar'}>
                      <IconButton size="small" onClick={() => togglePin(s.id, it.key)}
                        sx={{ p: 0.3, color: pin ? 'warning.main' : 'text.disabled' }}>
                        {pin ? <StarIcon sx={{ fontSize: 16 }} /> : <StarBorderIcon sx={{ fontSize: 16 }} />}
                      </IconButton>
                    </Tooltip>
                  </Box>
                );
              })}
            </Box>
          </Box>
        ))}
      </Popover>
    </Box>
  );
};

export default ToolbarIndicatorsGroup;
